import React from "react";
import { Typography, Card, Descriptions, Alert, Tag, Collapse, List, Space, Divider } from "antd";
import { 
    CheckCircleOutlined, 
    InfoCircleOutlined, 
    SettingOutlined, 
    BulbOutlined,
    DatabaseOutlined,
    MailOutlined,
    FileTextOutlined,
    ExclamationCircleOutlined
} from "@ant-design/icons";
import ExecutionResultsViewer from "./ExecutionResultsViewer";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

/**
 * 新Agent结果显示组件
 * 支持V4.1规范的suggested_payload结构
 */
const NewAgentResultViewer = ({ actionItem, currentFlightData }) => {
    const suggestedPayload = actionItem.suggested_payload;
    const userModifiedPayload = actionItem.user_modified_payload;
    
    // 如果不是V4.1格式，显示错误信息
    if (!suggestedPayload || suggestedPayload.version !== "4.1") {
        return (
            <Alert 
                message="数据格式不支持" 
                description="此行动项的数据格式不是V4.1规范，无法正确显示处理结果。" 
                type="warning" 
                showIcon 
            />
        );
    }

    const { reasoning, system_operations, context_data_summary } = suggestedPayload;

    // 获取用户决策信息
    const getUserDecision = (operationId) => {
        if (!userModifiedPayload?.system_operations) return null;
        const userOperation = userModifiedPayload.system_operations.find(
            op => op.operation_id === operationId
        );
        return userOperation?.op_meta?.decision || null;
    };

    // 操作类型图标映射
    const getOperationIcon = (operationType) => {
        const iconMap = {
            'CREATE_RECORD': <DatabaseOutlined style={{ color: '#52c41a' }} />,
            'UPDATE_RECORD': <SettingOutlined style={{ color: '#1890ff' }} />,
            'DELETE_RECORD': <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
            'SEND_EMAIL': <MailOutlined style={{ color: '#722ed1' }} />,
            'REPLY_EMAIL': <MailOutlined style={{ color: '#722ed1' }} />,
            'SEND_TEMPLATE_EMAIL': <MailOutlined style={{ color: '#722ed1' }} />,
            'LOG_INFO_ONLY': <FileTextOutlined style={{ color: '#faad14' }} />,
            'LOG_EVENT': <FileTextOutlined style={{ color: '#faad14' }} />,
            'ANALYSIS_ONLY': <BulbOutlined style={{ color: '#13c2c2' }} />,
            'CREATE_DRAFT_RECORDS': <DatabaseOutlined style={{ color: '#52c41a' }} />,
            'ACTIVATE_DRAFT_RECORDS': <CheckCircleOutlined style={{ color: '#52c41a' }} />,
            'DELETE_DRAFT_RECORDS': <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
            'CREATE_AND_NOTIFY': <MailOutlined style={{ color: '#722ed1' }} />,
            'INITIATE_WORKFLOW': <SettingOutlined style={{ color: '#1890ff' }} />
        };
        return iconMap[operationType] || <InfoCircleOutlined style={{ color: '#8c8c8c' }} />;
    };

    // 操作类型中文名称映射
    const getOperationTypeName = (operationType) => {
        const nameMap = {
            'CREATE_RECORD': '创建记录',
            'UPDATE_RECORD': '更新记录',
            'DELETE_RECORD': '删除记录',
            'SEND_EMAIL': '发送邮件',
            'REPLY_EMAIL': '回复邮件',
            'SEND_TEMPLATE_EMAIL': '发送模板邮件',
            'LOG_INFO_ONLY': '记录信息',
            'LOG_EVENT': '记录事件',
            'ANALYSIS_ONLY': '仅分析',
            'CREATE_DRAFT_RECORDS': '创建草稿记录',
            'ACTIVATE_DRAFT_RECORDS': '激活草稿记录',
            'DELETE_DRAFT_RECORDS': '删除草稿记录',
            'CREATE_AND_NOTIFY': '创建并通知',
            'INITIATE_WORKFLOW': '启动工作流'
        };
        return nameMap[operationType] || operationType;
    };

    // 渲染系统操作列表
    const renderSystemOperations = () => {
        if (!system_operations || system_operations.length === 0) {
            return <Alert message="无系统操作" type="info" showIcon />;
        }

        return (
            <List
                dataSource={system_operations}
                renderItem={(operation, index) => {
                    const userDecision = getUserDecision(operation.operation_id);
                    
                    return (
                        <List.Item>
                            <Card size="small" style={{ width: '100%' }}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Space>
                                        {getOperationIcon(operation.operation_type)}
                                        <Text strong>{getOperationTypeName(operation.operation_type)}</Text>
                                        {operation.target_service && (
                                            <Tag color="blue">{operation.target_service}</Tag>
                                        )}
                                        {/* 显示用户决策状态 */}
                                        {userDecision && (
                                            <Tag 
                                                color={userDecision === 'APPROVED' ? 'success' : 'error'}
                                                icon={userDecision === 'APPROVED' ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                                            >
                                                {userDecision === 'APPROVED' ? '用户批准' : '用户拒绝'}
                                            </Tag>
                                        )}
                                    </Space>
                                    
                                    {operation.operation_parameters && (
                                        <Descriptions size="small" column={1} bordered>
                                            {Object.entries(operation.operation_parameters).map(([key, value]) => (
                                                <Descriptions.Item key={key} label={key}>
                                                    {typeof value === 'object' ? 
                                                        JSON.stringify(value, null, 2) : 
                                                        String(value)
                                                    }
                                                </Descriptions.Item>
                                            ))}
                                        </Descriptions>
                                    )}
                                </Space>
                            </Card>
                        </List.Item>
                    );
                }}
            />
        );
    };

    // 渲染上下文数据摘要
    const renderContextSummary = () => {
        if (!context_data_summary) {
            return null;
        }

        return (
            <Card size="small" title="上下文信息" style={{ marginTop: 16 }}>
                <Descriptions size="small" column={2} bordered>
                    {context_data_summary.has_business_context !== undefined && (
                        <Descriptions.Item label="业务上下文" span={1}>
                            {context_data_summary.has_business_context ? '有' : '无'}
                        </Descriptions.Item>
                    )}
                    
                    {context_data_summary.email_id && (
                        <Descriptions.Item label="关联邮件ID" span={1}>
                            {context_data_summary.email_id}
                        </Descriptions.Item>
                    )}
                    
                    {context_data_summary.flight_details && (
                        <Descriptions.Item label="航班信息" span={2}>
                            <Space>
                                <Text strong>{context_data_summary.flight_details.flight_number}</Text>
                                <Text>{context_data_summary.flight_details.departure_airport}</Text>
                                <Text>→</Text>
                                <Text>{context_data_summary.flight_details.arrival_airport}</Text>
                            </Space>
                        </Descriptions.Item>
                    )}
                </Descriptions>
            </Card>
        );
    };

    return (
        <div>
            <Alert 
                message="此行动项已由新版AI Agent处理完成" 
                description={`Agent类型: ${actionItem.agent_type || '未知'}`}
                type="success" 
                showIcon 
                style={{ marginBottom: 16 }} 
            />

            <Collapse defaultActiveKey={['reasoning', 'operations']} ghost>
                {/* AI分析推理过程 */}
                <Panel 
                    header={
                        <Space>
                            <BulbOutlined />
                            <Text strong>AI分析推理过程</Text>
                        </Space>
                    } 
                    key="reasoning"
                >
                    <Card size="small">
                        <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                            {reasoning || '无分析推理信息'}
                        </Paragraph>
                    </Card>
                </Panel>

                {/* 系统操作列表 */}
                <Panel 
                    header={
                        <Space>
                            <SettingOutlined />
                            <Text strong>执行的系统操作</Text>
                            <Tag color="blue">{system_operations?.length || 0} 个操作</Tag>
                        </Space>
                    } 
                    key="operations"
                >
                    {renderSystemOperations()}
                </Panel>

                {/* 上下文信息 */}
                {context_data_summary && (
                    <Panel 
                        header={
                            <Space>
                                <InfoCircleOutlined />
                                <Text strong>上下文信息</Text>
                            </Space>
                        } 
                        key="context"
                    >
                        {renderContextSummary()}
                    </Panel>
                )}

                {/* 执行结果 - V3.0支持 */}
                {actionItem.additional_info?.execution_results && (
                    <Panel 
                        header={
                            <Space>
                                <CheckCircleOutlined />
                                <Text strong>执行结果</Text>
                                <Tag color="green">
                                    {actionItem.additional_info.execution_results.length} 个操作结果
                                </Tag>
                            </Space>
                        } 
                        key="execution_results"
                        defaultActiveKey={["execution_results"]}
                    >
                        <ExecutionResultsViewer 
                            executionResults={actionItem.additional_info.execution_results} 
                            showDetails={true} 
                        />
                    </Panel>
                )}
            </Collapse>
        </div>
    );
};

export default NewAgentResultViewer;